/* =====================================================================
 * Tabs — 공통 탭 네비게이션 컴포넌트 (테마 기반)
 * ===================================================================== */

import React from 'react';
import styled, { css } from 'styled-components';

export interface TabItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
}

interface TabsProps {
  items: TabItem[];
  activeId: string;
  onChange: (id: string) => void;
  className?: string;
  rightSlot?: React.ReactNode;
}

export const Tabs: React.FC<TabsProps> = ({
  items,
  activeId,
  onChange,
  className,
  rightSlot,
}) => (
  <TabBar className={className}>
    <TabList>
      {items.map((item) => (
        <Tab
          key={item.id}
          type="button"
          $active={item.id === activeId}
          onClick={() => onChange(item.id)}
        >
          {item.icon}
          {item.label}
        </Tab>
      ))}
    </TabList>
    {rightSlot && <RightSlot>{rightSlot}</RightSlot>}
  </TabBar>
);

const TabBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 0 4px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.colors.bgPrimary};
`;

const TabList = styled.div`
  display: flex;
  align-items: center;
`;

const Tab = styled.button<{ $active: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 12px 20px;
  border: none;
  background: none;
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: ${({ $active, theme }) => ($active ? theme.colors.textPrimary : theme.colors.textMuted)};
  cursor: pointer;
  transition: color ${({ theme }) => theme.transitions.fast};

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 12px;
    right: 12px;
    height: 2px;
    border-radius: 2px 2px 0 0;
    background: ${({ $active, theme }) => ($active ? theme.colors.accent : 'transparent')};
    transition: background ${({ theme }) => theme.transitions.fast};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const RightSlot = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding-right: 8px;
`;
